import { useEffect, useRef, useState } from "react";
import { Panel, Btn, inputCls, useOps } from "./ui";

// Admin-only audit: play every radio file in dial order with its frequency and filename.
export default function StationListen({ slots }) {
  const { api } = useOps();
  const list = [...slots.filter((s) => s.slot === "radio_static" || s.slot === "radio_tuning"),
    ...slots.filter((s) => s.slot.startsWith("station_")).sort((a, b) => a.slot.localeCompare(b.slot))];
  const [idx, setIdx] = useState(-1);
  const [run, setRun] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [secs, setSecs] = useState(8);
  const [problems, setProblems] = useState({});
  const audio = useRef(null);
  const timer = useRef(null);
  const cur = list[idx];

  const next = () => setIdx((i) => (i + 1 < list.length ? i + 1 : -1));
  const arm = () => { clearTimeout(timer.current); if (secs > 0) timer.current = setTimeout(next, secs * 1000); };
  useEffect(() => {
    clearTimeout(timer.current);
    const a = audio.current;
    if (!a) return;
    if (idx < 0 || !cur) { a.pause(); setPlaying(false); return; }
    if (!cur.filled) { setProblems((p) => ({ ...p, [cur.slot]: "no upload" })); timer.current = setTimeout(next, 600); return; }
    a.src = `${api.defaults.baseURL}/content/${cur.slot}/file?v=${encodeURIComponent(cur.updated_at || "")}`;
    a.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    arm();
    return () => clearTimeout(timer.current);
  }, [idx, run]); // eslint-disable-line react-hooks/exhaustive-deps

  const label = (s) => (s.slot.startsWith("station_") ? `${s.slot.slice(8)} MHz` : s.slot === "radio_static" ? "STATIC" : "TUNING");
  const name = (s) => s.label.replace(/^\d{3} — /, "");
  const pause = () => { audio.current?.pause(); clearTimeout(timer.current); setPlaying(false); };
  const resume = () => { audio.current?.play().then(() => { setPlaying(true); arm(); }).catch(() => {}); };
  const onError = () => {
    if (!cur) return;
    setProblems((p) => ({ ...p, [cur.slot]: "could not play" }));
    clearTimeout(timer.current);
    timer.current = setTimeout(next, 600);
  };

  return (
    <Panel title="Station listening mode" testid="station-listen" actions={<>
      <label className="flex items-center gap-1 text-[11px] text-slate-400">seconds each
        <input type="number" min="0" className={`${inputCls} w-14 py-0.5 text-xs`} value={secs} onChange={(e) => setSecs(Math.max(0, Number(e.target.value)))} data-testid="station-listen-seconds" />
      </label>
      <Btn onClick={() => { setProblems({}); setIdx(0); setRun((r) => r + 1); }} disabled={!list.length} data-testid="station-listen-play-all">Play all</Btn>
      <Btn variant="ghost" onClick={() => setIdx((i) => Math.max(0, i - 1))} disabled={idx <= 0} data-testid="station-listen-prev">Prev</Btn>
      {playing ? <Btn variant="ghost" onClick={pause} data-testid="station-listen-pause">Pause</Btn>
        : <Btn variant="ghost" onClick={resume} disabled={idx < 0} data-testid="station-listen-resume">Resume</Btn>}
      <Btn variant="ghost" onClick={next} disabled={idx < 0} data-testid="station-listen-next">Next</Btn>
      <Btn variant="ghost" onClick={() => setIdx(-1)} disabled={idx < 0} data-testid="station-listen-stop">Stop</Btn>
    </>}>
      <audio ref={audio} onEnded={() => secs === 0 && next()} onError={onError} className="hidden" data-testid="station-listen-audio" />
      <div data-testid="station-listen-now" className="mb-2 flex items-baseline gap-3 rounded bg-[#1a2332] px-3 py-2">
        {cur ? <>
          <span className="mono text-xl text-sky-300">{label(cur)}</span>
          <span className="text-sm text-slate-200">{name(cur)}</span>
          <span className="truncate text-[11px] text-slate-500">{cur.filename || "no upload"}</span>
          <span className="ml-auto mono text-[11px] text-slate-500">{idx + 1}/{list.length}</span>
        </> : <span className="text-xs text-slate-500">Play all steps through static, tuning and all 49 stations in dial order{secs ? ` (${secs}s each; 0 = full file)` : " (full files)"}.</span>}
      </div>
      <div className="grid grid-cols-2 gap-x-4 sm:grid-cols-3 lg:grid-cols-5">
        {list.map((s, i) => (
          <button key={s.slot} type="button" onClick={() => setIdx(i)} data-testid={`station-listen-item-${s.slot}`}
            className={`flex items-center gap-2 truncate py-0.5 text-left text-[11px] ${i === idx ? "text-sky-300" : problems[s.slot] ? "text-red-400" : "text-slate-400 hover:text-slate-200"}`}
            title={problems[s.slot] || s.filename || ""}>
            <span className="mono w-14 shrink-0">{label(s)}</span><span className="truncate">{name(s)}</span>
          </button>
        ))}
      </div>
      {Object.keys(problems).length > 0 && (
        <div data-testid="station-listen-problems" className="mt-2 text-[11px] text-red-400">
          Problems: {Object.entries(problems).map(([k, v]) => `${k} (${v})`).join(", ")}
        </div>
      )}
    </Panel>
  );
}
